import React, { useEffect, useState } from 'react';
import QRCode from "qrcode";
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Add, Delete, ContentCopy, QrCode2, Download } from '@mui/icons-material';
import { shortURLsAPI, formsAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';
import dayjs from 'dayjs';

export default function ShortURLs() {
  const { enqueueSnackbar } = useSnackbar();
  const [urls, setUrls] = useState([]);
  const [forms, setForms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [targetURL, setTargetURL] = useState('');
  const [selectedForm, setSelectedForm] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [qrFor, setQrFor] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrError, setQrError] = useState('');

  // Render a QR code (PNG data URL) for a short URL row
  const openQR = (u) => {
    setQrFor(u);
    setQrDataUrl('');
    setQrError('');
    QRCode.toDataURL(u.short_url, { width: 512, margin: 2, errorCorrectionLevel: 'M' })
      .then((d) => setQrDataUrl(d))
      .catch((e) => setQrError('Could not generate QR code'));
  };

  const downloadQR = () => {
    if (!qrDataUrl || !qrFor) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = 'qr-' + (qrFor.short_code || 'short-url') + '.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const load = () => {
    shortURLsAPI.list().then((r) => setUrls(r.data.data));
    formsAPI.list().then((r) => setForms(r.data.data));
  };
  useEffect(load, []);

  const handleCreate = async () => {
    try {
      const data = { target_url: targetURL, title: urlTitle };
      if (selectedForm) data.form_id = selectedForm;
      const res = await shortURLsAPI.create(data);
      enqueueSnackbar('Short URL created', { variant: 'success' });
      setDialogOpen(false);
      setTargetURL('');
      setSelectedForm('');
      setUrlTitle('');
      load();
      const created = res && res.data && res.data.data;
      if (created && created.short_url) openQR(created);
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Failed', { variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    await shortURLsAPI.delete(id);
    load();
  };

  const copy = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => enqueueSnackbar('Copied!'));
    } else {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); enqueueSnackbar('Copied!'); } catch(e) {}
      document.body.removeChild(ta);
    }
  };

  const createFromForm = (form) => {
    const url = `${window.location.origin}/apply/${form.id}`;
    setTargetURL(url);
    setSelectedForm(form.id);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Short URLs</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
          Create Short URL
        </Button>
      </Box>

      {/* Quick shortcuts for forms */}
      {forms.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Quick: Shorten a form link</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {forms.filter((f) => f.is_active).map((f) => (
                <Chip key={f.id} label={f.title} onClick={() => createFromForm(f)}
                  variant="outlined" clickable size="small" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Short URL</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Form</TableCell>
                <TableCell>Clicks</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {urls.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        /s/{u.short_code}
                      </Typography>
                      <IconButton size="small" onClick={() => copy(u.short_url)}>
                        <ContentCopy sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{u.title || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {u.target_url}
                  </TableCell>
                  <TableCell>{u.form_title || '—'}</TableCell>
                  <TableCell><Chip label={u.click_count} size="small" /></TableCell>
                  <TableCell>{dayjs(u.created_at).format('MMM D, YYYY')}</TableCell>
                  <TableCell>
                    <IconButton size="small" title="Show QR code" onClick={() => openQR(u)}>
                      <QrCode2 fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(u.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {urls.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No short URLs yet
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Short URL</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <TextField fullWidth label="Title (for internal tracking)" value={urlTitle}
            onChange={(e) => setUrlTitle(e.target.value)} sx={{ mb: 2 }}
            placeholder="e.g. Instagram promotion, Mailing list link" />
          <TextField fullWidth label="Target URL" value={targetURL}
            onChange={(e) => setTargetURL(e.target.value)} sx={{ mb: 2 }}
            placeholder="https://..." />
          <FormControl fullWidth>
            <InputLabel>Link to form (optional)</InputLabel>
            <Select value={selectedForm} label="Link to form (optional)"
              onChange={(e) => setSelectedForm(e.target.value)}>
              <MenuItem value="">None</MenuItem>
              {forms.map((f) => <MenuItem key={f.id} value={f.id}>{f.title}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!targetURL}>Create</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!qrFor} onClose={() => setQrFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>QR Code</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {qrFor && (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2, wordBreak: 'break-all' }}>
              {qrFor.short_url}
            </Typography>
          )}
          {qrError ? (
            <Typography variant="body2" color="error">{qrError}</Typography>
          ) : qrDataUrl ? (
            <Box>
              <img src={qrDataUrl} alt="QR code" style={{ width: '100%', maxWidth: 300 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Scanning this opens the short link (clicks are still counted).
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">Generating…</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrFor(null)}>Close</Button>
          {qrFor && <Button onClick={() => copy(qrFor.short_url)}>Copy link</Button>}
          <Button variant="contained" startIcon={<Download />} onClick={downloadQR} disabled={!qrDataUrl}>
            Download PNG
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
